import { Check as CheckIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import type React from 'react';
import { useThemeColors } from '../hooks/useThemeColors';
import { IMG } from '../theme/assets';
import AppDialog, { DialogField } from './AppDialog';

/** グループの色。設定と左メニューで同じ並びを使う */
export const GROUP_COLORS = [
  '#10b981', // emerald
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#f59e0b', // amber
  '#ef4444', // red
  '#ec4899', // pink
  '#14b8a6', // teal
  '#6366f1', // indigo
];

interface GroupFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** 作るのか、既にあるものを直すのか。見出しとボタンの文字だけが変わる */
  mode: 'create' | 'edit';
  name: string;
  onNameChange: (v: string) => void;
  description: string;
  onDescriptionChange: (v: string) => void;
  color: string;
  onColorChange: (v: string) => void;
  onSubmit: () => void;
  /** 送信中。ボタンを押せなくして、中に ◯ を出す */
  submitting?: boolean;
}

/**
 * グループを作る／直すモーダル。
 *
 * 同じ見た目のものが設定と左メニューに別々に書かれていて、片方だけ直すと食い違うので
 * ここ1か所にまとめた。入力中の値は呼び出し側が持つ（保存先が画面ごとに違うため）。
 */
const GroupFormDialog: React.FC<GroupFormDialogProps> = ({
  open,
  onClose,
  mode,
  name,
  onNameChange,
  description,
  onDescriptionChange,
  color,
  onColorChange,
  onSubmit,
  submitting = false,
}) => {
  const tokens = useThemeColors();
  const creating = mode === 'create';

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      icon={<Box component="img" src={IMG.settings.group} alt="" />}
      title={creating ? '新しいグループを作成' : 'グループを編集'}
      subtitle={creating ? '番組名や班の名前で作ります' : '名前・説明・色を変えます'}
      actions={
        <>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: tokens.muted }}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!name.trim() || submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: '10px',
              px: 2.5,
              background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
            }}
          >
            {submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : creating ? (
              '作成'
            ) : (
              '保存'
            )}
          </Button>
        </>
      }
    >
      <DialogField
        label="グループ名"
        placeholder="例: 制作1班 / ADちゃんズ"
        value={name}
        onChange={onNameChange}
        required
      />
      <DialogField
        label="説明"
        placeholder="例: 報道の制作1班。平日夕方の番組を担当"
        value={description}
        onChange={onDescriptionChange}
        multiline
        rows={2}
      />
      <DialogField label="テーマカラー" placeholder="" value="" onChange={() => {}}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {GROUP_COLORS.map((c) => (
            <Box
              key={c}
              onClick={() => onColorChange(c)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: c,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                boxShadow: color === c ? `0 0 0 3px ${tokens.paper}, 0 0 0 5px ${c}` : 'none',
              }}
            >
              {color === c && <CheckIcon sx={{ color: '#fff', fontSize: 19 }} />}
            </Box>
          ))}
        </Box>
      </DialogField>
    </AppDialog>
  );
};

export default GroupFormDialog;
